// Stage 33: Bulletin Canvas — history side drawer.
// Lists R2 objects under bulletin/<id>/, lets the user preview a previous
// version inside the same iframe by switching the version key.

import { useEffect, useState } from 'react';
import { getPanelHistory, getPanelRender } from './bulletinClient';
import { renderMarkdownToHtml } from './markdownRender';
import type { BulletinPanelDTO, PanelHistoryEntry } from './types';

// Stage 36: keep the History drawer's CSP in sync with
// BulletinPanelBody so historical versions of HTML containing
// worker-served image URLs still render in the drawer's preview.
const API_BASE = import.meta.env.VITE_API_BASE as string;
const CSP_META =
  '<meta http-equiv="Content-Security-Policy" ' +
  'content="default-src \'self\' \'unsafe-inline\' data:; ' +
  'script-src \'unsafe-inline\'; ' +
  'connect-src \'none\'; ' +
  `img-src data: ${API_BASE}; ` +
  'style-src \'unsafe-inline\';">';

function wrapWithCSP(body: string): string {
  if (/<head[^>]*>/i.test(body)) {
    return body.replace(/<head([^>]*)>/i, `<head$1>${CSP_META}`);
  }
  return `<!DOCTYPE html><html><head>${CSP_META}</head><body>${body}</body></html>`;
}

export function HistoryDrawer({
  panel,
  onClose,
}: {
  panel: BulletinPanelDTO;
  onClose: () => void;
}): JSX.Element {
  const [versions, setVersions] = useState<PanelHistoryEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [previewSrcdoc, setPreviewSrcdoc] = useState<string | null>(null);

  useEffect(() => {
    void getPanelHistory(panel.id)
      .then((vs) => setVersions(vs))
      .catch((e) =>
        setError(e instanceof Error ? e.message : String(e)),
      );
  }, [panel.id]);

  const loadVersion = async (key: string): Promise<void> => {
    setActiveKey(key);
    try {
      const res = await getPanelRender(panel.id, key);
      if (res.format === 'markdown') {
        setPreviewSrcdoc(wrapWithCSP(renderMarkdownToHtml(res.body)));
      } else {
        setPreviewSrcdoc(wrapWithCSP(res.body));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div
      className="bulletin-drawer-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="bulletin-drawer"
        onClick={(e) => e.stopPropagation()}
        aria-label={`History for ${panel.koji}`}
      >
        <header className="bulletin-drawer-header">
          <h3>
            <code>{panel.koji}</code> — History
          </h3>
          <button type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        {error ? <p className="bulletin-error">⚠ {error}</p> : null}

        {versions.length === 0 ? (
          <p className="muted">No historical versions yet.</p>
        ) : (
          <ul className="bulletin-history-list">
            {versions.map((v) => (
              <li key={v.key}>
                <button
                  type="button"
                  className={
                    activeKey === v.key
                      ? 'bulletin-history-active'
                      : ''
                  }
                  onClick={() => void loadVersion(v.key)}
                >
                  <span>{new Date(v.uploaded).toLocaleString()}</span>
                  <span className="muted small">{v.size} bytes</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {previewSrcdoc ? (
          <div className="bulletin-history-preview">
            <iframe
              sandbox="allow-scripts"
              srcDoc={previewSrcdoc}
              title={`${panel.koji} — preview`}
              className="bulletin-iframe"
            />
          </div>
        ) : null}

        <footer className="bulletin-drawer-footer">
          <button type="button" onClick={onClose}>
            Close
          </button>
        </footer>
      </aside>
    </div>
  );
}
